import * as React from 'react';
import { isEqual } from 'lodash';

import Select from './Select';
import { SelectProps, ItemType, ValueType } from './model';

export type { SelectProps };

/**
 * Select is an UI element to allow the user to make a selection out of the given <items>, by using a <WBItem> component
 *  that (on press) opens a native select component (using react-native-picker-select) .
 *
 * Example usecases:
 * - Used on the non-timeslot 'create reservation' screen.
 *
 * @example
 * <WBSelect label='User preferences' items={[
 *     {label: 'first option', value: 1},
 *     {label: 'second option', value: 2},
 * ]} value=2 />
 *
 */

export class WBSelect extends React.Component<SelectProps> {
	constructor(props: SelectProps) {
		super(props);

		this.getSingleItem = this.getSingleItem.bind(this);
		this.onChange = this.onChange.bind(this);
	}

	componentDidUpdate(prevProps: SelectProps) {
		const { items, onItemsChange } = this.props;

		// Check whether the items have been updated and fire a onItemsChange callback accordingly
		if (onItemsChange && !isEqual(prevProps.items, items)) {
			onItemsChange(items);
		}
	}

	getSingleItem(value: ValueType, items: ItemType[]): ItemType {
		if (typeof value === 'object') {
			return value as ItemType;
		}

		return items.find((item) => item.value == value);
	}

	getSelectedItems(value: ValueType, items: ItemType[]) {
		const { isMulti } = this.props;

		if (!items) {
			return isMulti ? [] : {};
		}

		if (isMulti) {
			return (value && (value as ItemType[]).map((item) => this.getSingleItem(item, items))) || [];
		}

		return this.getSingleItem(value, items);
	}

	onChange(value: ValueType) {
		const { isMulti, items, onChange, value: oldValue } = this.props;

		let newSelectedItems = this.getSelectedItems(value, items);

		if (newSelectedItems) {
			if (isMulti) {
				newSelectedItems = (newSelectedItems as ItemType[]).filter((i) => !i.disabled);
			} else if ((newSelectedItems as ItemType).disabled) {
				newSelectedItems = oldValue;
			}
		}

		onChange &&
			onChange.call(
				null,
				isMulti
					? (newSelectedItems as ItemType[]).map((item) => item.value)
					: (newSelectedItems as ItemType)?.value,
			);
	}

	render() {
		const { items, value } = this.props;
		const selectedValues = this.getSelectedItems(value, items) as ValueType;

		return <Select {...this.props} value={selectedValues} onChange={this.onChange} />;
	}
}
